/**
 * The owner's dashboard: the website and the app, live (server: routes/analytics.ts). Only for the emails in
 * ADMIN_EMAILS; everyone else gets "Page not found". Opening it leaves this browser out of the numbers.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { BarChart, HBars, LineChart, Sparkline } from '../components/charts';
import { Icons } from '../components/icons';
import { Badge, Button, Card, cx, Empty, ErrorNote, Loading, PageHeader, Segmented, Stat, Table, Td, Th } from '../components/ui';
import { analyticsOff } from '../lib/analytics';
import { api, ApiError, qs, useAction, useApi } from '../lib/api';
import { dateTime, num, pct, relative } from '../lib/format';
import { useSession } from '../lib/session';
import type { HeardFrom } from '../lib/types';
import { HEARD_FROM } from './Login';

type RangeKey = '24h' | '7d' | '30d' | '90d';

interface Totals {
  visitors: number;
  visits: number;
  pageviews: number;
  bounceRate: number;
  avgVisitMs: number;
  signups: number;
  verified: number;
}

interface Report {
  range: { from: string; to: string; tz: string; bucket: 'hour' | 'day' };
  totals: Totals & { paying: number; trialing: number };
  previous: Totals;
  series: { b: string; visitors: number; pageviews: number; active_users: number; signups: number }[];
  sources: { source: string; visitors: number; visits: number; pageviews: number; bounce: number; signups: number }[];
  campaigns: { utm_source: string | null; utm_medium: string | null; utm_campaign: string | null; visitors: number; visits: number; signups: number }[];
  pages: { path: string; views: number; visitors: number; entries: number; avg_ms: number | null; avg_scroll: number | null }[];
  clicks: { path: string; label: string | null; target: string | null; clicks: number; visitors: number }[];
  funnel: { step: string; n: number }[];
  signups: {
    id: string;
    name: string;
    email: string;
    createdAt: string;
    verified: boolean;
    heardFrom: HeardFrom | null;
    heardFromDetail: string | null;
    company: string | null;
    plan: string;
    trialEndsAt: string | null;
    lastSeen: string | null;
    appViews: number;
    appActions: number;
    aiTurns: number;
    source: string | null;
    campaign: string | null;
    landingPage: string | null;
    country: string | null;
    device: string | null;
  }[];
  heardFrom: { heard_from: HeardFrom | null; n: number }[];
  countries: { value: string; visitors: number }[];
  devices: { value: string; visitors: number }[];
  browsers: { value: string; visitors: number }[];
  os: { value: string; visitors: number }[];
  app: {
    totals: { users: number; companies: number; views: number; actions: number; time_ms: number };
    screens: { path: string; views: number; users: number; avg_ms: number | null }[];
    actions: { path: string; label: string | null; clicks: number; users: number }[];
    companies: { id: string; name: string; users: number; views: number; actions: number; last_seen: string; subscription_status: string | null }[];
  };
  recentVisits: {
    visitor: string;
    started: string | null;
    source: string;
    country: string | null;
    device: string | null;
    browser: string | null;
    engagedMs: number;
    signedUp: boolean;
    events: { ts: string; kind: string; path: string; label: string | null; durationMs: number | null; scroll: number | null }[];
  }[];
  pending: number;
}

interface Live {
  now: string;
  onSite: number;
  inApp: number;
  pages: { area: 'site' | 'app'; path: string; n: number }[];
  minutes: { m: string; visitors: number }[];
  feed: {
    id: number;
    ts: string;
    kind: string;
    area: 'site' | 'app';
    path: string;
    label: string | null;
    target: string | null;
    source: string | null;
    country: string | null;
    device: string | null;
    browser: string | null;
    visitor: string;
    email: string | null;
    company: string | null;
  }[];
}

interface GrowthReview {
  headline: string;
  happening: string[];
  working: string[];
  problems: string[];
  actions: { title: string; why: string; metric: string; impact: 'high' | 'medium' | 'low' }[];
  watch: string[];
  caveats: string[];
}
interface SavedReview {
  review: GrowthReview;
  createdAt: string;
  model: string;
  range: RangeKey;
}

const RANGES: { key: RangeKey; label: string }[] = [
  { key: '24h', label: '24 hours' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
];

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const regions = typeof Intl.DisplayNames === 'function' ? new Intl.DisplayNames(['en'], { type: 'region' }) : null;

/** "DE" → "🇩🇪 Germany". */
function country(code: string | null | undefined) {
  if (!code || !/^[A-Z]{2}$/.test(code)) return 'Unknown';
  const flag = String.fromCodePoint(...[...code].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
  return `${flag} ${regions?.of(code) ?? code}`;
}

function duration(ms: number | null | undefined) {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/** The change from the previous period, as a Stat's sub line. */
function delta(now: number, before: number, opts: { points?: boolean; lowerIsBetter?: boolean } = {}): { sub: string; tone?: 'good' | 'bad' } {
  if (opts.points) {
    const d = (now - before) * 100;
    if (!before && !now) return { sub: 'no data before' };
    return { sub: `${d >= 0 ? '+' : ''}${d.toFixed(1)} pts vs before`, tone: d === 0 ? undefined : (d > 0) !== !!opts.lowerIsBetter ? 'good' : 'bad' };
  }
  if (!before) return { sub: now ? 'new this period' : 'none before either' };
  const d = (now - before) / before;
  return { sub: `${d >= 0 ? '+' : ''}${(d * 100).toFixed(0)}% vs before`, tone: d === 0 ? undefined : (d > 0) !== !!opts.lowerIsBetter ? 'good' : 'bad' };
}

const bucketLabel = (b: string, bucket: 'hour' | 'day') => {
  if (bucket === 'hour') return `${b.slice(11, 13)}:00`;
  const [, m, d] = b.split('-').map(Number);
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m! - 1]} ${d}`;
};

export function AdminPage() {
  const { me } = useSession();
  const [range, setRange] = useState<RangeKey>('7d');
  const [counted, setCounted] = useState(!analyticsOff.get());
  // The owner's own visits would skew the numbers: this browser stops counting once it opens the dashboard.
  useEffect(() => {
    analyticsOff.set(true);
    setCounted(false);
  }, []);
  const report = useApi<Report>(me.admin ? `/api/admin/analytics${qs({ range, tz: TZ })}` : null, { refetchInterval: 60_000, placeholderData: (prev) => prev });
  const live = useApi<Live>(me.admin ? `/api/admin/live${qs({ tz: TZ })}` : null, { refetchInterval: 10_000 });

  if (!me.admin) {
    return (
      <div className="py-24 text-center">
        <div className="text-[15px] font-semibold">Page not found</div>
        <Link to="/" className="mt-2 inline-block text-[13px] text-brand hover:underline">
          Back to the command center
        </Link>
      </div>
    );
  }

  const twoFactor = report.error instanceof ApiError && report.error.code === 'two_factor_required';
  return (
    <div>
      <PageHeader
        eyebrow="Only you can see this page"
        title="Owner dashboard"
        description="Who visits raremargin.com, where they come from, what they click, who signs up, and what customers do in the app. Live: the top updates every 10 seconds, the rest every minute."
        actions={<Segmented items={RANGES} value={range} onChange={setRange} />}
      />
      {twoFactor ? (
        <Card title="Turn on two-factor sign-in first">
          <p className="text-[13.5px] text-ink-2">
            This page lists every sign-up and company, so it opens only for an account with two-factor sign-in.{' '}
            <Link to="/settings?tab=account" className="text-brand hover:underline">
              Settings, Your account
            </Link>
            .
          </p>
        </Card>
      ) : (
        <div className="space-y-5">
          <LiveCard live={live.data} error={live.error} />
          <ReviewCard range={range} />
          {report.error ? <ErrorNote error={report.error} /> : !report.data ? <Loading label="Counting…" className="py-16" /> : <ReportView r={report.data} />}
          <p className="text-[12px] text-muted">
            {counted ? 'This browser was being counted; it is not any more.' : 'Your own visits in this browser are not counted.'}{' '}
            <button
              type="button"
              className="underline underline-offset-2 hover:text-ink"
              onClick={() => {
                analyticsOff.set(false);
                setCounted(true);
              }}
            >
              Count them again
            </button>
            . Signed in as an owner, your visits inside the app are never counted.
          </p>
        </div>
      )}
    </div>
  );
}

// --- Live -----------------------------------------------------------------------------------------------------------

function describe(e: Live['feed'][number]) {
  switch (e.kind) {
    case 'pageview':
      return (
        <>
          opened <Path p={e.path} />
        </>
      );
    case 'click':
      return (
        <>
          clicked <span className="font-medium text-ink">“{e.label ?? '?'}”</span> on <Path p={e.path} />
          {e.target && e.target !== e.path && <span className="text-muted"> → {e.target}</span>}
        </>
      );
    case 'form_start':
      return (
        <>
          started filling the form on <Path p={e.path} />
        </>
      );
    case 'signup':
      return <span className="font-semibold text-good">signed up</span>;
    case 'checkout':
      return <span className="font-semibold text-good">opened checkout</span>;
    default:
      return e.kind;
  }
}

const Path = ({ p }: { p: string }) => <code className="rounded bg-sunken px-1 py-px text-[12px] text-ink-2">{p}</code>;

function LiveCard({ live, error }: { live?: Live; error: unknown }) {
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <span className="relative flex size-2">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-good opacity-60" />
            <span className="relative inline-flex size-2 rounded-full bg-good" />
          </span>
          Live
        </span>
      }
      subtitle="Anyone with activity in the last 5 minutes. The feed shows the latest page views, clicks and sign-ups."
    >
      {error ? (
        <ErrorNote error={error} />
      ) : !live ? (
        <Loading />
      ) : (
        <div className="grid gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="num text-[30px] leading-none font-semibold">{live.onSite}</div>
                <div className="mt-1 text-[12px] text-muted">on the website</div>
              </div>
              <div>
                <div className="num text-[30px] leading-none font-semibold">{live.inApp}</div>
                <div className="mt-1 text-[12px] text-muted">{live.inApp === 1 ? 'customer' : 'customers'} in the app</div>
              </div>
            </div>
            <div>
              <div className="mb-1 text-[11.5px] text-muted">People per minute, last 30 minutes</div>
              <Sparkline values={live.minutes.map((m) => m.visitors)} height={36} />
            </div>
            <div>
              <div className="mb-1.5 text-[11.5px] text-muted">Where they are now</div>
              {live.pages.length ? (
                <ul className="space-y-1 text-[12.5px]">
                  {live.pages.map((p) => (
                    <li key={`${p.area}${p.path}`} className="flex items-center justify-between gap-2">
                      <span className="min-w-0 truncate">
                        <Path p={p.path} /> {p.area === 'app' && <Badge tone="brand">app</Badge>}
                      </span>
                      <span className="num text-ink-2">{p.n}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-[12.5px] text-muted">Nobody right now.</div>
              )}
            </div>
          </div>
          <div className="max-h-[340px] min-w-0 overflow-y-auto scrollbar-thin">
            {live.feed.length ? (
              <ul className="divide-y divide-line/70">
                {live.feed.map((e) => (
                  <li key={e.id} className="flex items-start gap-3 py-2 text-[12.5px]">
                    <span className="num w-[62px] shrink-0 text-muted" title={dateTime(e.ts)}>
                      {relative(e.ts)}
                    </span>
                    <span className="min-w-0 flex-1 leading-relaxed">
                      <span className="text-ink-2">
                        {e.area === 'app' ? (
                          <span className="font-medium text-ink">
                            {e.email ?? 'A customer'}
                            {e.company && <span className="font-normal text-muted"> · {e.company}</span>}
                          </span>
                        ) : (
                          <span>
                            <span className="font-medium text-ink">Visitor {e.visitor}</span>
                            <span className="text-muted">
                              {e.country && ` · ${country(e.country)}`}
                              {e.device && ` · ${e.device}`}
                              {e.source && ` · from ${e.source}`}
                            </span>
                          </span>
                        )}{' '}
                        {describe(e)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty title="Nothing yet">Page views appear here within a few seconds of someone opening the site.</Empty>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// --- AI review ------------------------------------------------------------------------------------------------------

const IMPACT_TONE = { high: 'good', medium: 'info', low: 'neutral' } as const;

function ReviewCard({ range }: { range: RangeKey }) {
  const saved = useApi<{ enabled: boolean; last: SavedReview | null }>(`/api/admin/review${qs({ range })}`, { staleTime: Infinity });
  const [fresh, setFresh] = useState<SavedReview | null>(null);
  useEffect(() => setFresh(null), [range]);
  const run = useAction(() => api<SavedReview>('/api/admin/review', { body: { range, tz: TZ } }), { silent: true });
  const shown = fresh ?? saved.data?.last ?? null;
  const label = RANGES.find((r) => r.key === range)!.label.toLowerCase();
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Icons.Sparkle size={15} className="text-ai" /> AI review
        </span>
      }
      subtitle={`Reads the last ${label} of numbers below and tells you what is happening and what to work on next. It sees totals only, never names or emails, and runs only when you ask, on the server's OpenAI key (about 1–3¢ a review).`}
      actions={
        <Button variant="ai" loading={run.isPending} disabled={saved.data?.enabled === false} onClick={() => run.mutate(undefined, { onSuccess: (r) => setFresh(r) })}>
          {shown ? 'Review again' : `Review the last ${label}`}
        </Button>
      }
    >
      {saved.data?.enabled === false && <p className="text-[13px] text-muted">The server has no model key, so the review is off.</p>}
      {run.error ? <ErrorNote error={run.error} /> : null}
      {run.isPending && !shown && <Loading label="Reading the numbers…" />}
      {shown ? (
        <div className={cx('space-y-4', run.isPending && 'opacity-50')}>
          <div className="rounded-xl bg-ai-soft/60 px-4 py-3 text-[15px] leading-snug font-semibold text-ink">{shown.review.headline}</div>
          <div className="grid gap-4 md:grid-cols-3">
            <Points title="What is happening" items={shown.review.happening} />
            <Points title="Working" items={shown.review.working} tone="text-good" />
            <Points title="Not working" items={shown.review.problems} tone="text-bad" />
          </div>
          <div>
            <div className="app-eyebrow mb-2 text-[10.5px] text-muted">Do next</div>
            <ol className="space-y-2">
              {shown.review.actions.map((a, i) => (
                <li key={i} className="rounded-xl border border-line px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="num text-[12px] text-muted">{i + 1}.</span>
                    <span className="text-[13.5px] font-semibold text-ink">{a.title}</span>
                    <Badge tone={IMPACT_TONE[a.impact]}>{a.impact} impact</Badge>
                  </div>
                  <p className="mt-1 text-[13px] text-ink-2">{a.why}</p>
                  <p className="mt-1 text-[12px] text-muted">Measure it with: {a.metric}</p>
                </li>
              ))}
            </ol>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Points title="Watch next" items={shown.review.watch} />
            <Points title="How sure this is" items={shown.review.caveats} />
          </div>
          <p className="text-[11.5px] text-muted">
            {relative(shown.createdAt)} by {shown.model}. A model's reading of the numbers: check it against the tables below before acting on it.
          </p>
        </div>
      ) : (
        !run.isPending &&
        saved.data?.enabled !== false && <p className="text-[13px] text-muted">No review for this period yet. Press the button for one.</p>
      )}
    </Card>
  );
}

function Points({ title, items, tone }: { title: string; items: string[]; tone?: string }) {
  return (
    <div>
      <div className={cx('app-eyebrow mb-1.5 text-[10.5px]', tone ?? 'text-muted')}>{title}</div>
      {items.length ? (
        <ul className="space-y-1.5 text-[13px] leading-snug text-ink-2">
          {items.map((t, i) => (
            <li key={i} className="flex gap-2">
              <span className="mt-[7px] size-1 shrink-0 rounded-full bg-current opacity-50" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-[13px] text-muted">Nothing clear yet.</div>
      )}
    </div>
  );
}

// --- The period -----------------------------------------------------------------------------------------------------

function ReportView({ r }: { r: Report }) {
  const t = r.totals;
  const p = r.previous;
  const rate = t.visitors ? t.signups / t.visitors : null;
  const prevRate = p.visitors ? p.signups / p.visitors : 0;
  const labels = r.series.map((s) => bucketLabel(s.b, r.range.bucket));
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
        <Stat label="Visitors" value={num(t.visitors)} {...delta(t.visitors, p.visitors)}>
          <Def>People on the website, counted once a day.</Def>
        </Stat>
        <Stat label="Visits" value={num(t.visits)} {...delta(t.visits, p.visits)}>
          <Def>Runs of activity with no 30-minute gap.</Def>
        </Stat>
        <Stat label="Page views" value={num(t.pageviews)} {...delta(t.pageviews, p.pageviews)}>
          <Def>Pages opened on the website.</Def>
        </Stat>
        <Stat label="Bounce rate" value={pct(t.bounceRate, 0)} {...delta(t.bounceRate, p.bounceRate, { points: true, lowerIsBetter: true })}>
          <Def>Visits that saw one page and clicked nothing.</Def>
        </Stat>
        <Stat label="Time per visit" value={duration(t.avgVisitMs)} {...delta(t.avgVisitMs, p.avgVisitMs)}>
          <Def>Time the pages were on screen, on average.</Def>
        </Stat>
        <Stat label="Sign-ups" value={num(t.signups)} {...delta(t.signups, p.signups)}>
          <Def>New accounts. {num(t.verified)} confirmed their email.</Def>
        </Stat>
        <Stat label="Sign-up rate" value={pct(rate, 1)} {...(rate === null ? { sub: 'no visitors' } : delta(rate, prevRate, { points: true }))}>
          <Def>Sign-ups per visitor.</Def>
        </Stat>
        <Stat label="Paying now" value={num(t.paying)} sub={`${num(t.trialing)} in a trial`}>
          <Def>Companies with an active subscription.</Def>
        </Stat>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card title="Visitors and customers in the app" subtitle={<>Website visitors and customers active in the app, per {r.range.bucket}.</>}>
          <LineChart
            labels={labels}
            series={[
              { name: 'Website visitors', values: r.series.map((s) => s.visitors) },
              { name: 'Customers active in the app', values: r.series.map((s) => s.active_users) },
            ]}
            format={(v) => num(v)}
            area
            zeroBased
          />
        </Card>
        <Card title="Sign-ups" subtitle={<>New accounts per {r.range.bucket}.</>}>
          <BarChart labels={labels} series={[{ name: 'Sign-ups', values: r.series.map((s) => s.signups) }]} format={(v) => num(v)} />
        </Card>
      </div>

      <Funnel steps={r.funnel} />

      <Card title="Where visitors come from" subtitle={<>The site, ad or AI assistant each visit started from, and the sign-ups those visits led to. Direct: typed in, a bookmark, or an app that sends no referrer (ChatGPT's links often don't).</>} pad={false}>
        {r.sources.length ? (
          <Table>
            <thead>
              <tr>
                <Th>Source</Th>
                <Th right>Visitors</Th>
                <Th right>Visits</Th>
                <Th right>Bounce</Th>
                <Th right>Sign-ups</Th>
                <Th right>Sign-up rate</Th>
              </tr>
            </thead>
            <tbody>
              {r.sources.map((s) => (
                <tr key={s.source}>
                  <Td className="font-medium">{s.source}</Td>
                  <Td right mono>
                    {num(s.visitors)}
                  </Td>
                  <Td right mono>
                    {num(s.visits)}
                  </Td>
                  <Td right mono>
                    {pct(s.bounce, 0)}
                  </Td>
                  <Td right mono className={s.signups ? 'font-semibold text-good' : undefined}>
                    {num(s.signups)}
                  </Td>
                  <Td right mono>
                    {s.visitors ? pct(s.signups / s.visitors, 1) : '—'}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <NoData />
        )}
        {r.campaigns.length > 0 && (
          <div className="border-t border-line px-4 pt-3 pb-2">
            <div className="app-eyebrow mb-1 text-[10.5px] text-muted">Campaigns (UTM tags on your ad and post links)</div>
            <Table dense>
              <thead>
                <tr>
                  <Th>Source</Th>
                  <Th>Medium</Th>
                  <Th>Campaign</Th>
                  <Th right>Visitors</Th>
                  <Th right>Sign-ups</Th>
                </tr>
              </thead>
              <tbody>
                {r.campaigns.map((c, i) => (
                  <tr key={i}>
                    <Td>{c.utm_source ?? '—'}</Td>
                    <Td muted>{c.utm_medium ?? '—'}</Td>
                    <Td>{c.utm_campaign ?? '—'}</Td>
                    <Td right mono>
                      {num(c.visitors)}
                    </Td>
                    <Td right mono>
                      {num(c.signups)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      <div className="grid gap-5 xl:grid-cols-2">
        <Card title="Pages" subtitle={<>Entries: visits that started on the page. Time: on screen per view. Read: how far down people scrolled, on average.</>} pad={false}>
          {r.pages.length ? (
            <Table>
              <thead>
                <tr>
                  <Th>Page</Th>
                  <Th right>Views</Th>
                  <Th right>Visitors</Th>
                  <Th right>Entries</Th>
                  <Th right>Time</Th>
                  <Th w="110px">Read</Th>
                </tr>
              </thead>
              <tbody>
                {r.pages.map((pg) => (
                  <tr key={pg.path}>
                    <Td>
                      <Path p={pg.path} />
                    </Td>
                    <Td right mono>
                      {num(pg.views)}
                    </Td>
                    <Td right mono>
                      {num(pg.visitors)}
                    </Td>
                    <Td right mono>
                      {num(pg.entries)}
                    </Td>
                    <Td right mono>
                      {duration(pg.avg_ms)}
                    </Td>
                    <Td>
                      <ReadBar v={pg.avg_scroll} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <NoData />
          )}
        </Card>
        <Card title="What visitors click" subtitle={<>Links and buttons on the website, by page. “Goes to” is where a link leads.</>} pad={false}>
          {r.clicks.length ? (
            <Table>
              <thead>
                <tr>
                  <Th>Page</Th>
                  <Th>Clicked</Th>
                  <Th>Goes to</Th>
                  <Th right>Clicks</Th>
                  <Th right>People</Th>
                </tr>
              </thead>
              <tbody>
                {r.clicks.map((c, i) => (
                  <tr key={i}>
                    <Td>
                      <Path p={c.path} />
                    </Td>
                    <Td className="max-w-[220px] truncate font-medium">{c.label ?? '—'}</Td>
                    <Td muted className="max-w-[160px] truncate">
                      {c.target ?? '—'}
                    </Td>
                    <Td right mono>
                      {num(c.clicks)}
                    </Td>
                    <Td right mono>
                      {num(c.visitors)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <NoData />
          )}
        </Card>
      </div>

      <Signups r={r} />

      <AppUsage app={r.app} />

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        <Breakdown title="Countries" rows={r.countries.map((c) => ({ label: country(c.value === 'Unknown' ? null : c.value), value: c.visitors }))} />
        <Breakdown title="Devices" rows={r.devices.map((c) => ({ label: c.value, value: c.visitors }))} />
        <Breakdown title="Browsers" rows={r.browsers.map((c) => ({ label: c.value, value: c.visitors }))} />
        <Breakdown title="Operating systems" rows={r.os.map((c) => ({ label: c.value, value: c.visitors }))} />
      </div>

      <RecentVisits visits={r.recentVisits} />

      <Card title="How these numbers are measured">
        <ul className="list-disc space-y-1.5 pl-5 text-[13px] leading-relaxed text-ink-2">
          <li>RareMargin counts visits itself: no cookies, no outside service. A visitor is a hash of their address and browser with a salt that changes every day, so one person on two days counts as two visitors, and nobody can be traced back.</li>
          <li>Ad blockers that block scripts, and browsers without JavaScript, aren't counted. A sign-up from such a browser still counts, under the source “Not tracked”.</li>
          <li>A sign-up belongs to the visit it happened in, so its source is where that visit came from. Someone who came from Reddit yesterday and typed the address today counts as Direct: the “How did you hear” answers in the sign-ups table fill that gap.</li>
          <li>Inside the app, only screens and the app's own button text are recorded, never anything from a customer's books. Your own use isn't counted.</li>
          <li>Page views wait on the server and are saved when the database wakes (at least every 30 minutes); opening this page saves them at once. Raw events are kept 13 months.</li>
          <li>Times are in your time zone ({r.range.tz}).</li>
        </ul>
      </Card>
    </div>
  );
}

const Def = ({ children }: { children: ReactNode }) => <div className="mt-1.5 text-[11px] leading-snug text-muted">{children}</div>;
const NoData = () => <div className="px-4 py-8 text-center text-[13px] text-muted">Nothing in this period yet.</div>;

function ReadBar({ v }: { v: number | null }) {
  if (v === null) return <span className="text-muted">—</span>;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 flex-1 rounded-full bg-sunken">
        <div className={cx('h-full rounded-full', v >= 70 ? 'bg-good' : v >= 40 ? 'bg-brand' : 'bg-warn')} style={{ width: `${v}%` }} />
      </div>
      <span className="num w-8 text-right text-[12px] text-ink-2">{Math.round(v)}%</span>
    </div>
  );
}

function Funnel({ steps }: { steps: Report['funnel'] }) {
  const top = Math.max(steps[0]?.n ?? 0, 1);
  // The step that loses the largest share of the one before it, among steps with enough people to judge.
  let worst = -1;
  let worstRate = 1;
  steps.forEach((s, i) => {
    const prev = steps[i - 1];
    if (i && prev && prev.n >= 5 && s.n / prev.n < worstRate) {
      worst = i;
      worstRate = s.n / prev.n;
    }
  });
  return (
    <Card
      title="From visit to paying customer"
      subtitle={
        <>
          How many people reached each step in this period. The website steps count visitors; from “Signed up” on, the people who signed up in this period. The red step loses the largest share of the step
          before it.
        </>
      }
    >
      <ol className="space-y-2">
        {steps.map((s, i) => {
          const prev = steps[i - 1];
          return (
            <li key={s.step} className="grid grid-cols-[150px_minmax(0,1fr)_120px] items-center gap-3 text-[13px] sm:grid-cols-[190px_minmax(0,1fr)_150px]">
              <span className={cx('truncate', i === worst ? 'font-semibold text-bad' : 'text-ink')}>{s.step}</span>
              <div className="h-5 rounded-md bg-sunken">
                <div className={cx('h-full rounded-md', i === worst ? 'bg-bad/70' : i >= 4 ? 'bg-good/70' : 'bg-brand/70')} style={{ width: `${Math.max((s.n / top) * 100, s.n ? 1.5 : 0)}%` }} />
              </div>
              <span className="num text-right text-ink-2">
                <span className="font-semibold text-ink">{num(s.n)}</span>
                {prev && <span className="ml-1.5 text-[12px] text-muted">{prev.n ? pct(s.n / prev.n, 0) : '—'} of above</span>}
              </span>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

const PLAN_TONE: Record<string, 'good' | 'info' | 'warn' | 'bad' | 'neutral'> = { Paying: 'good', Trial: 'info', 'Trial ended': 'warn', 'past due': 'bad', cancelled: 'bad', 'on hold': 'warn' };

function Signups({ r }: { r: Report }) {
  const heard = r.heardFrom.map((h) => ({ label: h.heard_from ? (HEARD_FROM[h.heard_from]?.label ?? h.heard_from) : 'No answer', value: h.n }));
  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,3fr)_minmax(0,1fr)]">
      <Card title={`Sign-ups (${r.signups.length})`} subtitle={<>Everyone who signed up in this period, newest first: where they came from, what they answered, and what they have done since.</>} pad={false}>
        {r.signups.length ? (
          <div className="overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <Th>When</Th>
                  <Th>Who</Th>
                  <Th>Came from</Th>
                  <Th>How they heard</Th>
                  <Th>Plan</Th>
                  <Th right>In the app</Th>
                  <Th>Last seen</Th>
                </tr>
              </thead>
              <tbody>
                {r.signups.map((s) => (
                  <tr key={s.id}>
                    <Td muted className="whitespace-nowrap">
                      {dateTime(s.createdAt)}
                    </Td>
                    <Td>
                      <div className="font-medium text-ink">{s.name}</div>
                      <div className="text-[12px] text-muted">
                        {s.email} {s.verified ? <span className="text-good">✓</span> : <span title="Email not confirmed">(unconfirmed)</span>}
                      </div>
                      {s.company && <div className="text-[12px] text-ink-2">{s.company}</div>}
                    </Td>
                    <Td>
                      <div>{s.source ?? '—'}</div>
                      <div className="text-[12px] text-muted">
                        {[s.campaign, s.landingPage && `landed on ${s.landingPage}`, s.country && country(s.country), s.device].filter(Boolean).join(' · ')}
                      </div>
                    </Td>
                    <Td className="max-w-[220px]">
                      <div>{s.heardFrom ? (HEARD_FROM[s.heardFrom]?.label ?? s.heardFrom) : <span className="text-muted">—</span>}</div>
                      {s.heardFromDetail && <div className="text-[12px] text-muted italic">“{s.heardFromDetail}”</div>}
                    </Td>
                    <Td>
                      <Badge tone={PLAN_TONE[s.plan] ?? 'neutral'}>{s.plan}</Badge>
                    </Td>
                    <Td right className="text-[12px] whitespace-nowrap text-ink-2">
                      {num(s.appViews)} screens · {num(s.appActions)} clicks
                      <div className="text-muted">{num(s.aiTurns)} assistant messages</div>
                    </Td>
                    <Td muted className="whitespace-nowrap">
                      {relative(s.lastSeen)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <NoData />
        )}
      </Card>
      <Breakdown title="How they heard about RareMargin" explain="The optional question on the sign-up form." rows={heard} />
    </div>
  );
}

function AppUsage({ app }: { app: Report['app'] }) {
  const t = app.totals;
  return (
    <Card title="Inside the app" subtitle={<>What signed-in customers did: the screens they opened and the buttons they pressed. IDs in addresses show as :id.</>}>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="Active customers" value={num(t.users)} sub={`${num(t.companies)} ${t.companies === 1 ? 'company' : 'companies'}`} />
        <Stat label="Screens opened" value={num(t.views)} sub={t.users ? `${num(t.views / t.users, 1)} per person` : undefined} />
        <Stat label="Buttons pressed" value={num(t.actions)} sub={t.users ? `${num(t.actions / t.users, 1)} per person` : undefined} />
        <Stat label="Time in the app" value={duration(t.time_ms)} sub={t.users ? `${duration(t.time_ms / t.users)} per person` : undefined} />
        <Stat label="Companies active" value={num(app.companies.length)} />
      </div>
      <div className="mt-5 grid gap-5 xl:grid-cols-3">
        <MiniTable
          title="Screens"
          head={['Screen', 'Views', 'People', 'Time']}
          rows={app.screens.map((s) => [<Path key="p" p={s.path} />, num(s.views), num(s.users), duration(s.avg_ms)])}
        />
        <MiniTable title="Buttons" head={['Button', 'On', 'Clicks', 'People']} rows={app.actions.map((a) => [a.label ?? '—', <Path key="p" p={a.path} />, num(a.clicks), num(a.users)])} />
        <MiniTable
          title="Companies"
          head={['Company', 'People', 'Clicks', 'Last seen']}
          rows={app.companies.map((c) => [
            <span key="n" className="font-medium">
              {c.name}
              {c.subscription_status === 'active' && (
                <Badge tone="good" className="ml-1.5">
                  paying
                </Badge>
              )}
            </span>,
            num(c.users),
            num(c.actions),
            relative(c.last_seen),
          ])}
        />
      </div>
    </Card>
  );
}

function MiniTable({ title, head, rows }: { title: string; head: string[]; rows: ReactNode[][] }) {
  return (
    <div className="min-w-0">
      <div className="app-eyebrow mb-1.5 text-[10.5px] text-muted">{title}</div>
      {rows.length ? (
        <div className="max-h-[360px] overflow-y-auto scrollbar-thin">
          <Table dense>
            <thead>
              <tr>
                {head.map((h, i) => (
                  <Th key={h} right={i > 0}>
                    {h}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <Td key={j} right={j > 0} mono={j > 0 && typeof cell === 'string'} className={j === 0 ? 'max-w-[200px] truncate' : undefined}>
                      {cell}
                    </Td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="py-6 text-center text-[13px] text-muted">Nothing yet.</div>
      )}
    </div>
  );
}

function Breakdown({ title, rows, explain }: { title: string; rows: { label: ReactNode; value: number }[]; explain?: string }) {
  const total = rows.reduce((s, r) => s + r.value, 0);
  return (
    <Card title={title} subtitle={explain}>
      {rows.length ? <HBars rows={rows.map((r) => ({ ...r, sub: total ? pct(r.value / total, 0) : undefined }))} format={(v) => num(v)} /> : <div className="text-[13px] text-muted">Nothing yet.</div>}
    </Card>
  );
}

const KIND_ICON: Record<string, string> = { pageview: '→', click: '●', form_start: '✎', signup: '★', checkout: '$', leave: '' };

function RecentVisits({ visits }: { visits: Report['recentVisits'] }) {
  return (
    <Card title="Latest visits, step by step" subtitle={<>The last 25 visits to the website: each page opened (with time on it and how far down it was read) and each click, in order.</>}>
      {visits.length ? (
        <ul className="divide-y divide-line/70">
          {visits.map((v) => (
            <li key={`${v.visitor}${v.started}`} className="py-3">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px]">
                <span className="font-medium text-ink">Visitor {v.visitor}</span>
                <span className="text-muted">{relative(v.started)}</span>
                <Badge>{v.source}</Badge>
                <span className="text-muted">{[v.country && country(v.country), v.device, v.browser].filter(Boolean).join(' · ')}</span>
                <span className="text-muted">· {duration(v.engagedMs)} on screen</span>
                {v.signedUp && <Badge tone="good">signed up</Badge>}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-1 text-[12px]">
                {v.events
                  .map((e, i) => ({ e, i }))
                  .filter(({ e }) => e.kind !== 'leave')
                  .map(({ e, i }) => {
                    // The page's time and depth: every report it sent before the next page (one each time its tab was hidden).
                    const next = v.events.findIndex((x, j) => j > i && x.kind === 'pageview');
                    const reports = e.kind === 'pageview' ? v.events.slice(i + 1, next < 0 ? undefined : next).filter((x) => x.kind === 'leave' && x.path === e.path) : [];
                    const left = reports.length
                      ? { durationMs: reports.reduce((s, x) => s + (x.durationMs ?? 0), 0), scroll: Math.max(...reports.map((x) => x.scroll ?? 0)) }
                      : undefined;
                    return (
                      <span
                        key={i}
                        className={cx('inline-flex items-center gap-1 rounded-md px-1.5 py-0.5', e.kind === 'pageview' ? 'bg-sunken text-ink-2' : e.kind === 'signup' ? 'bg-good-soft text-good' : 'bg-brand-soft/60 text-brand')}
                      >
                        <span aria-hidden>{KIND_ICON[e.kind] ?? '·'}</span>
                        {e.kind === 'pageview' ? e.path : e.kind === 'click' ? `“${e.label ?? '?'}”` : e.kind === 'form_start' ? 'started form' : e.kind}
                        {left?.durationMs != null && (
                          <span className="text-muted">
                            {duration(left.durationMs)}
                            {left.scroll != null && `, ${left.scroll}%`}
                          </span>
                        )}
                      </span>
                    );
                  })}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <NoData />
      )}
    </Card>
  );
}
